/**
 * Drug category rules for medication task documentation extras.
 */

const ANALGESICS = [
  'paracetamol',
  'acetaminophen',
  'tramadol',
  'morphine',
  'codeine',
  'ibuprofen',
  'naproxen',
  'ketorolac',
  'fentanyl',
  'oxycodone',
  'diclofenac',
  'celecoxib',
];

const ANTIHYPERTENSIVES = [
  'metoprolol',
  'amlodipine',
  'furosemide',
  'captopril',
  'enalapril',
  'losartan',
  'ramipril',
  'bisoprolol',
  'carvedilol',
  'atenolol',
  'nifedipine',
  'verapamil',
  'hydralazine',
  'digoxin',
];

const INSULIN = [
  'insulin',
  'actrapid',
  'lantus',
  'novorapid',
  'mixtard',
  'glargine',
  'detemir',
  'aspart',
  'regular insulin',
  'nph insulin',
  'nph',
];

const ANTICOAGULANTS = [
  'warfarin',
  'enoxaparin',
  'heparin',
  'clexane',
  'fragmin',
  'rivaroxaban',
  'apixaban',
  'dabigatran',
];

export const DRUG_CATEGORIES = {
  ANALGESIC: 'analgesic',
  ANTIHYPERTENSIVE: 'antihypertensive',
  INSULIN: 'insulin',
  ANTICOAGULANT: 'anticoagulant',
  IV_INFUSION: 'iv_infusion',
  OTHER: 'other',
};

function normalizeDrugName(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function matchesAny(drugName, list) {
  const name = normalizeDrugName(drugName);
  if (!name) return false;
  return list.some((entry) => {
    const token = normalizeDrugName(entry);
    return name === token || name.includes(token) || token.includes(name);
  });
}

function hasVolumeHint(med) {
  const dose = `${med?.dose || ''} ${med?.dose_unit || ''} ${med?.volume || ''}`.toLowerCase();
  return /\d\s*ml\b|\bml\b|\bcc\b|volume|infusion/.test(dose) || med?.volume != null;
}

export function getDrugCategory(med) {
  const name = med?.drug_name || med?.name || '';
  const route = String(med?.route || '').toUpperCase();

  if (matchesAny(name, ANALGESICS)) return DRUG_CATEGORIES.ANALGESIC;
  if (matchesAny(name, ANTIHYPERTENSIVES)) return DRUG_CATEGORIES.ANTIHYPERTENSIVE;
  if (matchesAny(name, INSULIN)) return DRUG_CATEGORIES.INSULIN;
  if (matchesAny(name, ANTICOAGULANTS)) return DRUG_CATEGORIES.ANTICOAGULANT;
  if (route === 'IV' && hasVolumeHint(med)) return DRUG_CATEGORIES.IV_INFUSION;
  return DRUG_CATEGORIES.OTHER;
}

function hasValue(value) {
  return value != null && String(value).trim() !== '';
}

/**
 * @returns {{ key: string, labelKey: string, required: boolean }[]}
 */
export function getMedicationExtraFields(med) {
  const category = getDrugCategory(med);
  const route = String(med?.route || '').toUpperCase();

  if (category === DRUG_CATEGORIES.ANALGESIC) {
    return [
      { key: 'pain_before', labelKey: 'painScoreBefore', required: true },
      { key: 'pain_after', labelKey: 'painScoreAfter', required: false },
    ];
  }

  if (category === DRUG_CATEGORIES.ANTIHYPERTENSIVE) {
    return [
      { key: 'bp_before', labelKey: 'bpBefore', required: true, kind: 'bp' },
      { key: 'pulse_before', labelKey: 'pulseOptional', required: false },
    ];
  }

  if (category === DRUG_CATEGORIES.INSULIN) {
    return [
      { key: 'blood_sugar_before', labelKey: 'bloodSugarBefore', required: true },
      { key: 'blood_sugar_after', labelKey: 'bloodSugarAfter', required: false },
    ];
  }

  if (category === DRUG_CATEGORIES.ANTICOAGULANT) {
    const fields = [{ key: 'inr', labelKey: 'inrOptional', required: false }];
    if (route === 'SC' || route === 'SQ' || route === 'SUBCUT') {
      fields.push({ key: 'injection_site', labelKey: 'injectionSite', required: false });
    }
    return fields;
  }

  if (category === DRUG_CATEGORIES.IV_INFUSION) {
    return [
      { key: 'infusion_rate', labelKey: 'infusionRateOptional', required: false },
      { key: 'volume', labelKey: 'volumeOptional', required: false },
    ];
  }

  return [];
}

export function isBpBeforeComplete(med) {
  if (hasValue(med?.bp_systolic_before) && hasValue(med?.bp_diastolic_before)) {
    return true;
  }
  if (!hasValue(med?.bp_before)) return false;
  return /^\s*\d{2,3}\s*\/\s*\d{2,3}\s*$/.test(String(med.bp_before).trim());
}

export function isMedicationExtraFieldFilled(med, field) {
  if (!field) return true;
  if (field.kind === 'bp' || field.key === 'bp_before') {
    return isBpBeforeComplete(med);
  }
  return hasValue(med?.[field.key]);
}

/**
 * @returns {null | { fieldKey: string, messageKey: string }}
 */
export function getMedicationRequiredExtraBlocker(med) {
  const fields = getMedicationExtraFields(med);
  const missing = fields.find((field) => field.required && !isMedicationExtraFieldFilled(med, field));
  if (!missing) return null;

  if (missing.key === 'pain_before') {
    return { fieldKey: missing.key, messageKey: 'recordPainScoreBeforeSubmit' };
  }
  if (missing.key === 'blood_sugar_before') {
    return { fieldKey: missing.key, messageKey: 'recordBloodSugarBeforeSubmit' };
  }
  if (missing.key === 'bp_before') {
    return { fieldKey: missing.key, messageKey: 'recordBloodPressureBeforeSubmit' };
  }
  return { fieldKey: missing.key, messageKey: null };
}

export function getNoteMedicationSubmitBlocker(note) {
  const meds = note?.medications_administered?.length
    ? note.medications_administered
    : note?.medications || [];
  for (const med of meds) {
    const blocker = getMedicationRequiredExtraBlocker(med);
    if (blocker) return blocker;
  }
  return null;
}

/** Merge spoken/generated extras onto a prefilled MAR med without wiping core fields. */
export function mergeMedicationExtras(existingMed, incomingMed = {}) {
  const base = { ...(existingMed || {}) };
  const incoming = incomingMed || {};
  const preferExisting = (key) => {
    if (hasValue(base[key])) return base[key];
    return incoming[key] ?? base[key];
  };

  return {
    ...base,
    drug_name: preferExisting('drug_name') || preferExisting('name') || '',
    name: preferExisting('name') || preferExisting('drug_name') || '',
    dose: preferExisting('dose'),
    dose_amount: hasValue(base.dose_amount) ? base.dose_amount : incoming.dose_amount ?? null,
    dose_unit: preferExisting('dose_unit'),
    route: preferExisting('route'),
    time_given: preferExisting('time_given'),
    patient_response: preferExisting('patient_response') || preferExisting('response'),
    adverse_reaction: base.adverse_reaction ?? incoming.adverse_reaction ?? false,
    adverse_reaction_details: preferExisting('adverse_reaction_details'),
    pain_before: incoming.pain_before ?? base.pain_before ?? null,
    pain_after: incoming.pain_after ?? base.pain_after ?? null,
    bp_before: incoming.bp_before ?? base.bp_before ?? null,
    bp_systolic_before: incoming.bp_systolic_before ?? base.bp_systolic_before ?? null,
    bp_diastolic_before: incoming.bp_diastolic_before ?? base.bp_diastolic_before ?? null,
    pulse_before: incoming.pulse_before ?? base.pulse_before ?? null,
    blood_sugar_before: incoming.blood_sugar_before ?? base.blood_sugar_before ?? null,
    blood_sugar_after: incoming.blood_sugar_after ?? base.blood_sugar_after ?? null,
    inr: incoming.inr ?? base.inr ?? null,
    injection_site: incoming.injection_site ?? base.injection_site ?? null,
    infusion_rate: incoming.infusion_rate ?? base.infusion_rate ?? null,
    volume: incoming.volume ?? base.volume ?? null,
    confidence: base.confidence || incoming.confidence || 'high',
  };
}

/** Lightweight transcript parse for spoken extra values. */
export function applySpokenMedicationExtras(med, transcript) {
  const text = String(transcript || '');
  if (!text.trim()) return med;
  const next = { ...med };
  const needsBp = !isBpBeforeComplete(next);

  const painBefore = text.match(
    /pain(?:\s+score)?(?:\s+before)?(?:\s+(?:was|is|of|:))?\s*(\d{1,2})(?:\s*\/\s*10)?/i
  );
  if (painBefore && next.pain_before == null) {
    next.pain_before = Number(painBefore[1]);
  }

  const painAfter = text.match(
    /pain(?:\s+score)?\s+after(?:\s+(?:was|is|of|:))?\s*(\d{1,2})(?:\s*\/\s*10)?/i
  );
  if (painAfter && next.pain_after == null) {
    next.pain_after = Number(painAfter[1]);
  }

  const bp = text.match(
    /(?:bp|blood\s*pressure|ضغط)(?:\s+before)?(?:\s+(?:was|is|:))?\s*(\d{2,3})\s*(?:\/|over)\s*(\d{2,3})/i
  ) || (needsBp
    ? text.match(/\b(\d{2,3})\s*(?:\/|over)\s*(\d{2,3})\b/i)
    : null);
  if (bp && needsBp) {
    next.bp_systolic_before = Number(bp[1]);
    next.bp_diastolic_before = Number(bp[2]);
    next.bp_before = `${bp[1]}/${bp[2]}`;
  }

  const pulse = text.match(/(?:pulse|hr|heart\s*rate|نبض)(?:\s+before)?(?:\s+(?:was|is|:))?\s*(\d{2,3})/i);
  if (pulse && next.pulse_before == null) {
    next.pulse_before = Number(pulse[1]);
  }

  const sugarBefore = text.match(
    /(?:blood\s*sugar|glucose|rbs|sugar|سكر)(?:\s+before)?(?:\s+(?:was|is|:))?\s*(\d{2,3})/i
  );
  if (sugarBefore && next.blood_sugar_before == null) {
    next.blood_sugar_before = Number(sugarBefore[1]);
  }

  const sugarAfter = text.match(
    /(?:blood\s*sugar|glucose|rbs|sugar|سكر)\s+after(?:\s+(?:was|is|:))?\s*(\d{2,3})/i
  );
  if (sugarAfter && next.blood_sugar_after == null) {
    next.blood_sugar_after = Number(sugarAfter[1]);
  }

  const inr = text.match(/\binr\b(?:\s+(?:was|is|:))?\s*([\d.]+)/i);
  if (inr && next.inr == null) {
    next.inr = Number(inr[1]);
  }

  // Spoken time for empty time_given: "at 9:22", "time 09:22", "الساعة 9:22"
  if (!hasValue(next.time_given)) {
    const timeMatch = text.match(
      /(?:at|time|given|الساعة|وقت)?\s*\b([01]?\d|2[0-3])[:.]([0-5]\d)\b/i
    );
    if (timeMatch) {
      next.time_given = `${String(timeMatch[1]).padStart(2, '0')}:${timeMatch[2]}`;
    }
  }

  // Normalize spoken route words onto empty route
  if (!hasValue(next.route)) {
    if (/\b(oral|po|by\s*mouth|فموي)\b/i.test(text)) next.route = 'PO';
    else if (/\b(iv|intravenous|وريدي)\b/i.test(text)) next.route = 'IV';
    else if (/\b(im|intramuscular)\b/i.test(text)) next.route = 'IM';
    else if (/\b(sc|subcut|subcutaneous)\b/i.test(text)) next.route = 'SC';
  } else if (/^oral$/i.test(String(next.route).trim())) {
    next.route = 'PO';
  }

  return next;
}

/**
 * Apply spoken transcript onto existing MAR meds, prioritizing missing required fields
 * (e.g. "bp 120/80" → bp_before) instead of creating a separate vitals card.
 */
export function applySpokenExtrasToExistingNotes(generatedNotes = [], transcript) {
  const text = String(transcript || '').trim();
  if (!text || !generatedNotes?.length) {
    return { notes: generatedNotes || [], filledAny: false, filledKeys: [] };
  }

  const filledKeys = new Set();
  let filledAny = false;

  const notes = generatedNotes.map((entry) => {
    if (entry.type !== 'medication_admin' || !entry.note) return entry;
    const meds = entry.note.medications_administered?.length
      ? entry.note.medications_administered
      : entry.note.medications || [];
    if (!meds.length) return entry;

    const nextMeds = meds.map((med) => {
      const before = { ...(med || {}) };
      const after = applySpokenMedicationExtras(before, text);
      const fields = getMedicationExtraFields(after);
      fields.forEach((field) => {
        const key = field.key;
        const wasEmpty = !isMedicationExtraFieldFilled(before, field);
        const nowFilled = isMedicationExtraFieldFilled(after, field);
        if (wasEmpty && nowFilled) {
          filledAny = true;
          filledKeys.add(key);
          if (key === 'bp_before') filledKeys.add('bp');
          if (key === 'pulse_before') filledKeys.add('pulse');
          if (key === 'blood_sugar_before' || key === 'blood_sugar_after') {
            filledKeys.add('blood_sugar');
          }
          if (key === 'pain_before' || key === 'pain_after') filledKeys.add('pain');
        }
      });
      if (!hasValue(before.time_given) && hasValue(after.time_given)) {
        filledAny = true;
        filledKeys.add('time_given');
      }
      if (!hasValue(before.route) && hasValue(after.route)) {
        filledAny = true;
        filledKeys.add('route');
      }
      return after;
    });

    return {
      ...entry,
      note: {
        ...entry.note,
        medications_administered: nextMeds,
        medications: nextMeds,
      },
      error: null,
    };
  });

  return { notes, filledAny, filledKeys: Array.from(filledKeys) };
}

/** True when the recording is mostly vital/extra values meant for MAR fields. */
export function isLikelyMedicationExtrasOnlyTranscript(transcript) {
  const raw = String(transcript || '').trim();
  if (!raw) return false;
  const withoutExtras = raw
    .replace(/(?:bp|blood\s*pressure|ضغط)(?:\s+before)?(?:\s+(?:was|is|:))?\s*\d{2,3}\s*(?:\/|over)\s*\d{2,3}/gi, ' ')
    .replace(/\b\d{2,3}\s*(?:\/|over)\s*\d{2,3}\b/gi, ' ')
    .replace(/pain(?:\s+score)?(?:\s+before|\s+after)?(?:\s+(?:was|is|of|:))?\s*\d{1,2}(?:\s*\/\s*10)?/gi, ' ')
    .replace(/(?:pulse|hr|heart\s*rate|نبض)(?:\s+before)?(?:\s+(?:was|is|:))?\s*\d{2,3}/gi, ' ')
    .replace(/(?:blood\s*sugar|glucose|rbs|sugar|سكر)(?:\s+before|\s+after)?(?:\s+(?:was|is|:))?\s*\d{2,3}/gi, ' ')
    .replace(/\binr\b(?:\s+(?:was|is|:))?\s*[\d.]+/gi, ' ')
    .replace(/(?:at|time|given|الساعة|وقت)?\s*\b(?:[01]?\d|2[0-3])[:.][0-5]\d\b/gi, ' ')
    .replace(/\b(oral|po|iv|im|sc|by\s*mouth)\b/gi, ' ')
    .replace(/[^a-z\u0600-\u06FF\s]/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const leftover = withoutExtras.split(/\s+/).filter(Boolean);
  return leftover.length <= 2;
}

/**
 * Drop or strip a vitals note when its values were already applied to MAR extras.
 */
export function filterVitalsConsumedByMarExtras(entry, filledKeys = []) {
  if (!entry || entry.type !== 'vitals' || !entry.note) return entry;
  const filled = new Set(filledKeys);
  if (!filled.size) return entry;

  const vitals = { ...(entry.note.vitals || {}) };
  if (filled.has('bp') || filled.has('bp_before')) {
    delete vitals.bp_systolic;
    delete vitals.bp_diastolic;
  }
  if (filled.has('pulse') || filled.has('pulse_before')) {
    delete vitals.pulse;
  }
  if (filled.has('blood_sugar') || filled.has('blood_sugar_before')) {
    delete vitals.blood_sugar;
  }
  if (filled.has('pain') || filled.has('pain_before')) {
    delete vitals.pain;
  }

  const remainingKeys = [
    vitals.bp_systolic != null || vitals.bp_diastolic != null ? 'bp' : null,
    vitals.pulse != null ? 'pulse' : null,
    vitals.temp_celsius != null ? 'temp_celsius' : null,
    vitals.o2_sat != null ? 'o2_sat' : null,
    vitals.rr != null ? 'rr' : null,
    vitals.pain != null ? 'pain' : null,
    vitals.weight_kg != null ? 'weight_kg' : null,
    vitals.blood_sugar != null ? 'blood_sugar' : null,
  ].filter(Boolean);

  if (!remainingKeys.length) return null;

  return {
    ...entry,
    note: {
      ...entry.note,
      vitals,
      _manualVisibleVitalsKeys: remainingKeys,
    },
  };
}
